// The holder phase instance (sdk ADR-010, sdk ADR-014, sdk ADR-015).
import {
  applyEvent,
  INITIAL_PHASE,
} from '../auth/authState.js';
import type {
  CredentialIdentity,
  HolderEvent,
  HolderPhase,
  SignedOutReason,
  TransitionResult,
} from '../auth/authState.js';
import { expiresAtOf, identityOf, materialOf } from '../lifecycle/capabilities.js';
import { emitLifecycleEvent } from '../lifecycle/observability.js';
import type {
  CredentialUnreadableCause,
  PersistedCredential,
  SDKStorage,
} from '../storage/types.js';
import { PERSISTED_CREDENTIAL_VERSION, SDK_STORAGE_NAMESPACE } from '../storage/types.js';

/** The SDK-owned storage key for the persisted credential. */
export const HOLDER_PHASE_KEY = `${SDK_STORAGE_NAMESPACE}holder-phase`;

export interface CredentialUnreadable {
  cause: CredentialUnreadableCause;
  /** The version the blob claimed, or null when it could not be read at all. */
  version: number | null;
}

export type PersistedCredentialRead =
  | { readable: true; credential: PersistedCredential }
  | { readable: false; unreadable: CredentialUnreadable };

/** The ONE writer of the persisted shape (sdk ADR-014). */
export function buildPersistedCredential(
  credential: Omit<PersistedCredential, 'version'>,
): string {
  return JSON.stringify({
    version: PERSISTED_CREDENTIAL_VERSION,
    material: credential.material,
    identity: credential.identity,
    expires_at: credential.expires_at,
  } satisfies PersistedCredential);
}

/** The ONE reader; an unreadable blob is reported, never thrown, so the user signs in again
 *  instead of being locked out (.claude/rules/no-fallbacks.md exception, sdk ADR-014). */
export function readPersistedCredential(raw: string): PersistedCredentialRead {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { readable: false, unreadable: { cause: 'malformed', version: null } };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { readable: false, unreadable: { cause: 'malformed', version: null } };
  }
  const blob = parsed as Partial<PersistedCredential>;
  const version = typeof blob.version === 'number' ? blob.version : null;
  if (version !== PERSISTED_CREDENTIAL_VERSION) {
    return { readable: false, unreadable: { cause: 'version_mismatch', version } };
  }
  const { material, identity, expires_at: expiresAt } = blob;
  if (
    typeof material?.session_jwt !== 'string' ||
    typeof material.session_token !== 'string' ||
    typeof identity?.org_id !== 'string' ||
    typeof identity.account_id !== 'string' ||
    typeof identity.user_id !== 'string' ||
    typeof identity.email !== 'string' ||
    typeof expiresAt !== 'number'
  ) {
    return { readable: false, unreadable: { cause: 'malformed', version } };
  }
  return {
    readable: true,
    credential: { version, material, identity, expires_at: expiresAt },
  };
}

/** The emitter's half of the `ClockExpiry` precondition (sdk ADR-020). */
function hasExpired(phase: HolderPhase, now: number): boolean {
  const expiresAt = expiresAtOf(phase);
  return expiresAt !== null && expiresAt * 1000 <= now;
}

/** Restore-time `ClockExpiry` (sdk ADR-014, sdk ADR-020). */
function expireAtRestore(phase: HolderPhase, now: number): HolderPhase {
  if (phase.phase !== 'usable') return phase;
  if (!hasExpired(phase, now)) return phase;
  const result = applyEvent(phase, { type: 'ClockExpiry', now });
  if (result.refusal !== null || !result.changed) return phase;
  return result.phase;
}

/** The three legal restore outcomes (sdk ADR-014). */
export function derivePhaseFromCredential(
  credential: PersistedCredential,
  now: number,
): HolderPhase {
  const acquired = applyEvent(INITIAL_PHASE, {
    type: 'CredentialAcquired',
    material: credential.material,
    identity: credential.identity,
    expires_at: credential.expires_at,
  });
  if (acquired.refusal !== null) {
    throw new Error(
      `holder-store: the reducer refused a restored credential — ${acquired.refusal.reason}`,
    );
  }
  return expireAtRestore(acquired.phase, now);
}

/** The one origin-scoped bus every realm of a profile joins (sdk ADR-015). */
export const IDENTITY_CHANNEL_NAME = `${SDK_STORAGE_NAMESPACE}identity`;

/** The runtime channel resolution reads from (sdk ADR-015). */
export interface IdentityBusRuntime {
  BroadcastChannel?: typeof BroadcastChannel;
}

/** The applied event as it rides the bus, material elided (sdk ADR-015). */
export type IdentityWireEvent =
  | Extract<
      HolderEvent,
      {
        type:
          | 'AcquisitionFailed'
          | 'ActivationSucceeded'
          | 'ActivationFailed'
          | 'ClockExpiry'
          | 'LocalClear'
          | 'RemoteClear'
          | 'VerdictObserved';
      }
    >
  | { type: 'CredentialAcquired'; identity: CredentialIdentity; expires_at: number }
  | { type: 'CredentialRenewed'; expires_at: number };

export interface IdentityBusMessage {
  v: 1;
  event: IdentityWireEvent;
}

function assertNeverWire(value: never): never {
  throw new Error(
    `sdk.identity: the holder event union grew without the bus — ${JSON.stringify(value)}`,
  );
}

/** Null for the two in-flight events, which are not shared truth (sdk ADR-015). */
export function wireEventFor(event: HolderEvent): IdentityWireEvent | null {
  switch (event.type) {
    case 'SignInStarted':
    case 'CredentialAcquiredUnactivated':
      return null;
    case 'CredentialAcquired':
      return {
        type: 'CredentialAcquired',
        identity: event.identity,
        expires_at: event.expires_at,
      };
    case 'CredentialRenewed':
      return { type: 'CredentialRenewed', expires_at: event.expires_at };
    case 'ActivationFailed':
    case 'ClockExpiry':
    case 'LocalClear':
    case 'RemoteClear':
    case 'VerdictObserved':
      return event;
    default:
      return assertNeverWire(event);
  }
}

function runtimeShape(runtime: IdentityBusRuntime): string {
  const scope = runtime as Record<string, unknown>;
  if (typeof scope.window === 'object' && scope.window !== null) return 'browser';
  if (typeof scope.process === 'object' && scope.process !== null) return 'node';
  return 'unknown';
}

/** An absent channel is a loud construction failure (.claude/rules/no-fallbacks.md,
 *  sdk ADR-015). */
export function resolveIdentityChannel(
  name: string,
  runtime: IdentityBusRuntime,
  realm: string | null,
): BroadcastChannel {
  const ctor = runtime.BroadcastChannel;
  if (typeof ctor !== 'function') {
    emitLifecycleEvent('identity.broadcast_unavailable', {
      realm,
      runtime_shape: runtimeShape(runtime),
    });
    throw new Error(
      'sdk.identity: this runtime provides no BroadcastChannel, so a sibling realm could ' +
        'never learn that this one signed out',
    );
  }
  const channel = new ctor(name);
  // (sdk ADR-015)
  const unref = (channel as unknown as { unref?: () => void }).unref;
  if (typeof unref === 'function') unref.call(channel);
  return channel;
}

/** Whether a transition originated here or arrived on the bus (sdk ADR-015). */
export type TransitionOrigin = 'local' | 'observed';

/** The (org, account, user) tuple only — a rename is not a re-tenant (sdk ADR-010, sdk ADR-011). */
export function isIdentityChange(from: HolderPhase, to: HolderPhase): boolean {
  const before = identityOf(from);
  const after = identityOf(to);
  if (before === null || after === null) return false;
  return (
    before.org_id !== after.org_id ||
    before.account_id !== after.account_id ||
    before.user_id !== after.user_id
  );
}

export interface IdentityBusConfig {
  channelName?: string;
  runtime?: IdentityBusRuntime;
}

let ephemeralSeq = 0;

/** Named after the storage the realms share (sdk ADR-015). */
function defaultChannelName(storage: SDKStorage | null): string {
  const scope = storage === null ? `ephemeral:${(ephemeralSeq += 1)}` : storage.scopeId;
  return `${IDENTITY_CHANNEL_NAME}:${scope}`;
}

export class HolderStore {
  private current: HolderPhase = INITIAL_PHASE;
  private readonly subscribers = new Set<(phase: HolderPhase) => void>();
  private ticker: ReturnType<typeof setInterval> | null = null;
  private unreadable: CredentialUnreadable | null = null;

  private onNoCredential: (() => Promise<void>) | null = null;

  private onIdentityChange: (() => Promise<void>) | null = null;

  private readonly channel: BroadcastChannel;

  constructor(
    private readonly storage: SDKStorage | null,
    private readonly clockIntervalMs: number = 15_000,
    private readonly onTick?: () => void,
    startTicker = true,
    onNoCredential?: () => Promise<void>,
    private readonly realm: string | null = null,
    bus: IdentityBusConfig = {},
    onIdentityChange?: () => Promise<void>,
  ) {
    if (onNoCredential) {
      this.onNoCredential = onNoCredential;
    }
    if (onIdentityChange) {
      this.onIdentityChange = onIdentityChange;
    }
    this.channel = resolveIdentityChannel(
      bus.channelName ?? defaultChannelName(storage),
      bus.runtime ?? (globalThis as IdentityBusRuntime),
      realm,
    );
    this.channel.onmessage = (message: MessageEvent) => {
      const data = message.data as IdentityBusMessage | undefined;
      if (data?.v !== 1) {
        // (sdk ADR-028)
        emitLifecycleEvent('identity.bus_message_unsupported', {
          realm,
          version: typeof data?.v === 'number' ? data.v : null,
        });
        return;
      }
      const event = this.eventForWire(data.event);
      if (event === null) return;
      void this.applyObserved(event);
    };
    this.current = this.restore();
    // The only `ClockExpiry` producer, and the renewal cadence (sdk ADR-010, sdk ADR-020).
    if (startTicker) {
      this.ticker = setInterval(() => {
        const now = Date.now();
        if (hasExpired(this.current, now)) {
          void this.apply({ type: 'ClockExpiry', now });
        }
        this.onTick?.();
      }, this.clockIntervalMs);
    }
  }

  get phase(): HolderPhase {
    return this.current;
  }

  /** The discarded blob's cause, for the host's user-visible warning. */
  get restoreDiagnostic(): CredentialUnreadable | null {
    return this.unreadable;
  }

  subscribe(fn: (phase: HolderPhase) => void): () => void {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  /** Apply an event that originated in THIS realm (sdk ADR-015). */
  async apply(event: HolderEvent): Promise<TransitionResult> {
    return this.applyWithOrigin(event, 'local');
  }

  /** Apply an event another realm applied, arriving on the bus (sdk ADR-015). */
  async applyObserved(event: HolderEvent): Promise<TransitionResult> {
    return this.applyWithOrigin(event, 'observed');
  }

  /** A daemon `no_credential` frame; the clear is machine-wide (sdk ADR-015). */
  async observeDaemonClear(reason: SignedOutReason): Promise<void> {
    await this.applyWithOrigin({ type: 'RemoteClear', reason }, 'local');
    if (this.storage !== null) {
      this.storage.removeItem(HOLDER_PHASE_KEY);
    }
  }

  /** The one SDK-originated `LocalClear`, asserted by `renew.test.ts` (sdk ADR-011). */
  async clear(reason: SignedOutReason): Promise<void> {
    await this.apply({ type: 'LocalClear', reason });
    if (this.storage !== null) {
      this.storage.removeItem(HOLDER_PHASE_KEY);
    }
  }

  /** Stop the clock and leave the bus; the client's lifetime owns this
   *  (sdk ADR-001, sdk ADR-015). */
  dispose(): void {
    if (this.ticker !== null) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
    this.subscribers.clear();
    this.channel.onmessage = null;
    this.channel.close();
  }

  /** (sdk ADR-029) */
  private applyWithOrigin(
    event: HolderEvent,
    origin: TransitionOrigin,
  ): Promise<TransitionResult> {
    const run = this.applyChain.then(() => this.applyExclusive(event, origin));
    this.applyChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private applyChain: Promise<unknown> = Promise.resolve();

  private async applyExclusive(
    event: HolderEvent,
    origin: TransitionOrigin,
  ): Promise<TransitionResult> {
    const result = applyEvent(this.current, event);
    if (result.refusal !== null) {
      // The ONLY place a two-realm divergence is visible.
      emitLifecycleEvent('identity.transition_refused', {
        realm: this.realm,
        phase: result.refusal.from,
        event: result.refusal.event,
        reason: result.refusal.reason,
        origin,
      });
      return result;
    }
    if (!result.changed) {
      return result;
    }
    // The transition-to-no_credential barrier: one gate, every route (sdk ADR-010).
    if (result.phase.phase === 'no_credential' && this.onNoCredential) {
      await this.onNoCredential();
    }
    // The identity-change barrier: the same gate for an in-place re-tenant (sdk ADR-010, sdk ADR-011).
    if (this.onIdentityChange && isIdentityChange(this.current, result.phase)) {
      await this.onIdentityChange();
    }
    this.current = result.phase;
    if (origin === 'local') {
      if (event.type !== 'RemoteClear' && event.type !== 'ClockExpiry') {
        this.persist(result.phase);
      }
      this.broadcast(event);
    }
    for (const fn of this.subscribers) {
      fn(result.phase);
    }
    return result;
  }

  private broadcast(event: HolderEvent): void {
    const wire = wireEventFor(event);
    if (wire === null) return;
    this.channel.postMessage({ v: 1, event: wire } satisfies IdentityBusMessage);
  }

  /** Posted `LocalClear` applies as `RemoteClear`; null when the elided material is
   *  already gone (sdk ADR-015). */
  private eventForWire(wire: IdentityWireEvent): HolderEvent | null {
    switch (wire.type) {
      case 'LocalClear':
        return { type: 'RemoteClear', reason: wire.reason };
      case 'CredentialAcquired': {
        const stored = this.sharedCredential(wire.type, wire.expires_at, wire.identity);
        return stored === null
          ? null
          : {
              type: 'CredentialAcquired',
              material: stored.material,
              identity: stored.identity,
              expires_at: stored.expires_at,
            };
      }
      case 'CredentialRenewed': {
        const stored = this.sharedCredential(wire.type, wire.expires_at, null);
        return stored === null
          ? null
          : { type: 'CredentialRenewed', material: stored.material, expires_at: stored.expires_at };
      }
      default:
        return wire;
    }
  }

  /** (sdk ADR-030) */
  private sharedCredential(
    wireType: IdentityWireEvent['type'],
    expectedExpiresAt: number,
    expectedIdentity: CredentialIdentity | null,
  ): PersistedCredential | null {
    const raw = this.storage === null ? null : this.storage.getItem(HOLDER_PHASE_KEY);
    const read = raw === null ? null : readPersistedCredential(raw);
    if (read === null || !read.readable) {
      emitLifecycleEvent('identity.observed_event_dropped', {
        realm: this.realm,
        phase: this.current.phase,
        event: wireType,
        reason: 'material_unavailable',
      });
      return null;
    }
    const stored = read.credential;
    const agrees =
      stored.expires_at === expectedExpiresAt &&
      (expectedIdentity === null ||
        (stored.identity.org_id === expectedIdentity.org_id &&
          stored.identity.account_id === expectedIdentity.account_id));
    if (!agrees) {
      emitLifecycleEvent('identity.observed_event_dropped', {
        realm: this.realm,
        phase: this.current.phase,
        event: wireType,
        reason: 'wire_identity_mismatch',
      });
      return null;
    }
    return stored;
  }

  private persist(phase: HolderPhase): void {
    if (this.storage === null) return;
    const material = materialOf(phase);
    const identity = identityOf(phase);
    const expires_at = expiresAtOf(phase);
    if (material === null || identity === null || expires_at === null) {
      // (sdk ADR-014)
      this.storage.removeItem(HOLDER_PHASE_KEY);
      return;
    }
    this.storage.setItem(
      HOLDER_PHASE_KEY,
      buildPersistedCredential({ material, identity, expires_at }),
    );
  }

  private restore(): HolderPhase {
    if (this.storage === null) return INITIAL_PHASE;
    const raw = this.storage.getItem(HOLDER_PHASE_KEY);
    if (raw === null) return INITIAL_PHASE;
    const read = readPersistedCredential(raw);
    if (!read.readable) {
      // Discarded and surfaced, the degradation .claude/rules/no-fallbacks.md sanctions (sdk ADR-014).
      this.unreadable = read.unreadable;
      this.storage.removeItem(HOLDER_PHASE_KEY);
      emitLifecycleEvent('identity.credential_unreadable', {
        realm: this.realm,
        cause: read.unreadable.cause,
        version: read.unreadable.version,
      });
      return INITIAL_PHASE;
    }
    return derivePhaseFromCredential(read.credential, Date.now());
  }
}
